import { mdiArrowTopRightBottomLeft, mdiDatabaseOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Divider, Stack, StackProps, SxProps, Typography } from '@mui/material';
import React, { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IEplassProjectData } from '../../../../redux/user/sliceUserProjects';
import { vars } from '../../../../Theme';
import AppTooltip from '../../../General/AppTooltip/AppTooltip';
import { IProjectContextMenuConfig } from './ProjectContextMenu';
import ProjectFavouriteButton from './ProjectFavouriteButton';
import ProjectRowTag from './ProjectRowTag';
import i18n from '../../../../localization/i18n';

/** Style rules for the 'open' hover button. */
const btnSx: SxProps = {
    minWidth: 0,
    height: 36,
    width: 36,
    ml: 2,
    p: 0,
    '&:hover': {
        bgcolor: vars.lightGray,
    },
};

/**
 * Component that shows a user project as a simple row.
 */
export default (props: IProjectRowProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectSelectionView' });

    /** Allows the component to navigate to other views in the application.  */
    const navigate = useNavigate();

    /** State slice for controlling the visibility of the project row's 'open' button. */
    const [showOpenButton, setShowOpenButton] = useState<boolean>(false);

    /** Deconstruct props. */
    const { project, favourite, style, contextMenu } = props;
    const { category, replicaId, title, module } = project;

    /** Event handlers for controlling context menu and hover buttons. */
    const stackCallbacks: StackProps = {
        onContextMenu: e => contextMenu.callback(e, project),
        onMouseEnter: () => setShowOpenButton(true),
        onMouseLeave: () => setShowOpenButton(false),
    };

    /** Base styles for a project row. */
    const rowSx: SxProps = {
        '&:hover': { bgcolor: vars.bgGreen, cursor: 'pointer' },
        transition: 'all 0.5s',
        borderRadius: 1,
        minHeight: 56,
    };

    /**
     * Shows the row's open button if the state slice allows it.
     */
    const getOpenBtn = () =>
        showOpenButton ? (
            <AppTooltip title={t('open')} placement='top-start'>
                <Button
                    sx={btnSx}
                    onClick={() => {
                        navigate(`/project/${replicaId}`);
                    }}
                >
                    {<Icon path={mdiArrowTopRightBottomLeft} size={1.1} color={vars.font} />}
                </Button>
            </AppTooltip>
        ) : (
            <></>
        );

    return (
        <Stack
            direction='column'
            style={style}
            onClick={() => navigate(`/project/${replicaId}`)}
            {...stackCallbacks}
            sx={[
                {
                    alignItems: 'start',
                },
                ...(Array.isArray(rowSx) ? rowSx : [rowSx]),
                stackCallbacks.sx,
            ]}
        >
            <Stack
                direction='row'
                sx={{
                    alignItems: 'center',
                    flexGrow: 4,
                    pl: 1,
                    width: '100%',
                }}
            >
                <Stack
                    direction='row'
                    sx={{
                        alignItems: 'center',
                        flexGrow: 4,
                    }}
                >
                    <Stack
                        direction='row'
                        spacing={1}
                        sx={{
                            alignItems: 'start',
                        }}
                    >
                        <Icon path={mdiDatabaseOutline} size={1} color={vars.font} />

                        <Stack direction='column'>
                            <Typography
                                variant='h6'
                                sx={{
                                    fontSize: 16,
                                }}
                            >
                                {title}
                            </Typography>

                            <Typography
                                variant='body2'
                                sx={{
                                    color: vars.concrete,
                                }}
                            >
                                {category.split('\\')[0]}
                            </Typography>
                        </Stack>
                    </Stack>

                    {getOpenBtn()}
                </Stack>

                <Stack
                    direction='row'
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'end',
                    }}
                >
                    <ProjectRowTag
                        moduleAbbreviation={module.moduleAbbreviation}
                        tagColor={module.tagColor}
                        tooltipTitle={module.translations[i18n.language]}
                        key={replicaId}
                    />

                    <ProjectFavouriteButton replicaId={replicaId} favourite={favourite} />
                </Stack>
            </Stack>
            <Divider flexItem />
        </Stack>
    );
};

/**
 * Interface that describes the properties for the ProjectRow component.
 */
export interface IProjectRowProps {
    /** Target Eplass Project. */
    project: IEplassProjectData;

    /** CSS styles. */
    style: CSSProperties;

    /** Project favourite status. */
    favourite: boolean;

    /** Context Menu Configuration. */
    contextMenu: IProjectContextMenuConfig;
}
